/**
 * IframeEmbedExtension — Generic TipTap node for trusted iframe embeds.
 *
 * Validates URLs against the embed allowlist before rendering.
 * Supports: Our World in Data, YouTube, Vimeo, Tableau, World Bank,
 * UN SDG Tracker, Global Forest Watch, and other approved sources.
 *
 * Each embed stores: src (original URL), domain label, height, and
 * an optional caption. The allowlist determines which domains are
 * permitted and provides default sizing.
 */

import { Node, mergeAttributes } from '@tiptap/react';
import { matchAllowedDomain, DEFAULT_SANDBOX } from './embedAllowlist';
import type { AllowedEmbedDomain } from './embedAllowlist';

export interface IframeEmbedOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    iframeEmbed: {
      setIframeEmbed: (options: { src: string; caption?: string }) => ReturnType;
    };
  }
}

export const IframeEmbed = Node.create<IframeEmbedOptions>({
  name: 'iframeEmbed',
  group: 'block',
  content: '',
  draggable: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: { default: null },
      domainLabel: { default: '' },
      height: { default: 600 },
      sandbox: { default: DEFAULT_SANDBOX },
      caption: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-iframe-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, height, sandbox, caption, domainLabel, ...rest } = HTMLAttributes;
    // 'none' means no sandbox (needed for heavy JS apps like GFW maps)
    const iframeAttrs: Record<string, any> = {
      src,
      frameborder: '0',
      loading: 'lazy',
      allow: 'web-share; clipboard-write',
      style: `width:100%;height:${height}px;border:0;border-radius:8px;`,
    };
    if (sandbox && sandbox !== 'none') {
      iframeAttrs.sandbox = sandbox;
    }
    const children: any[] = [
      ['iframe', mergeAttributes(rest, iframeAttrs)],
    ];
    if (caption) {
      children.push(['figcaption', {}, caption]);
    }
    return ['div', mergeAttributes(this.options.HTMLAttributes, { 'data-iframe-embed': '' }), ...children];
  },

  addCommands() {
    return {
      setIframeEmbed:
        (options) =>
        ({ commands }) => {
          const match = matchAllowedDomain(options.src);
          if (!match) return false;
          return commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              domainLabel: match.label,
              height: match.defaultHeight,
              sandbox: match.sandbox ?? DEFAULT_SANDBOX,
              caption: options.caption || '',
            },
          });
        },
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentAttrs = { ...node.attrs };

      const dom = document.createElement('div');
      dom.setAttribute('data-iframe-embed', '');
      dom.style.margin = '16px 0';
      dom.style.position = 'relative';

      // Top bar: source badge + delete button
      const topBar = document.createElement('div');
      topBar.style.cssText =
        'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;';

      // Source badge (shows domain label)
      const badge = document.createElement('div');
      badge.style.cssText =
        'font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;' +
        'color:var(--text-muted);display:flex;align-items:center;gap:6px;';
      badge.innerHTML = `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--accent-primary);"></span>${currentAttrs.domainLabel || 'Embed'}`;
      topBar.appendChild(badge);

      // Delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.textContent = '\u2715';
      deleteBtn.title = 'Remove embed';
      deleteBtn.style.cssText =
        'background:rgba(220,38,38,0.1);border:1px solid rgba(220,38,38,0.25);border-radius:4px;' +
        'color:#dc2626;font-size:11px;width:22px;height:22px;cursor:pointer;display:flex;' +
        'align-items:center;justify-content:center;padding:0;line-height:1;transition:background 0.15s;';
      deleteBtn.addEventListener('mouseenter', () => { deleteBtn.style.background = 'rgba(220,38,38,0.2)'; });
      deleteBtn.addEventListener('mouseleave', () => { deleteBtn.style.background = 'rgba(220,38,38,0.1)'; });
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        const resolvedNode = editor.view.state.doc.nodeAt(pos);
        if (!resolvedNode) return;
        editor.view.dispatch(
          editor.view.state.tr.delete(pos, pos + resolvedNode.nodeSize)
        );
        editor.view.focus();
      });
      topBar.appendChild(deleteBtn);

      dom.appendChild(topBar);

      // Iframe container
      const container = document.createElement('div');
      container.style.cssText =
        'position:relative;width:100%;border-radius:8px;background:#000;';

      const iframe = document.createElement('iframe');
      iframe.src = currentAttrs.src;
      iframe.frameBorder = '0';
      iframe.loading = 'lazy';
      // 'none' = no sandbox (needed for heavy JS apps like GFW maps)
      if (currentAttrs.sandbox && currentAttrs.sandbox !== 'none') {
        iframe.sandbox.value = currentAttrs.sandbox;
      }
      iframe.allow = 'web-share; clipboard-write; fullscreen; autoplay; geolocation';
      iframe.style.cssText = `width:100%;height:${currentAttrs.height}px;border:0;display:block;border-radius:8px;`;
      container.appendChild(iframe);
      dom.appendChild(container);

      // Helper: update attribute
      function setAttr(key: string, value: string | number) {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        const resolvedNode = editor.view.state.doc.nodeAt(pos);
        if (!resolvedNode) return;
        editor.view.dispatch(
          editor.view.state.tr.setNodeMarkup(pos, undefined, {
            ...resolvedNode.attrs,
            [key]: value,
          })
        );
      }

      // Caption input
      const captionInput = document.createElement('input');
      captionInput.type = 'text';
      captionInput.placeholder = 'Add a caption…';
      captionInput.value = currentAttrs.caption || '';
      captionInput.style.cssText =
        'width:100%;border:none;outline:none;background:transparent;font-size:13px;font-style:italic;' +
        'color:var(--text-secondary);padding:6px 0 2px;font-family:inherit;box-sizing:border-box;';
      captionInput.addEventListener('blur', () => {
        if (captionInput.value !== currentAttrs.caption) {
          setAttr('caption', captionInput.value);
        }
      });
      dom.appendChild(captionInput);

      return {
        dom,
        stopEvent: (event: Event) => {
          const target = event.target;
          if (target === captionInput || target === iframe || target === deleteBtn) return true;
          return false;
        },
        ignoreMutation: () => true,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'iframeEmbed') return false;
          currentAttrs = { ...updatedNode.attrs };
          iframe.src = currentAttrs.src;
          iframe.style.height = `${currentAttrs.height}px`;
          badge.innerHTML = `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--accent-primary);"></span>${currentAttrs.domainLabel || 'Embed'}`;
          if (document.activeElement !== captionInput && captionInput.value !== currentAttrs.caption) {
            captionInput.value = currentAttrs.caption || '';
          }
          return true;
        },
      };
    };
  },
});
